import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  exportAllClientsExcel,
  exportSelectedClientsExcel,
  exportAllClientsPdf,
  exportSelectedClientsPdf,
  updateClientsFromExcel,
  importClientsFromExcel,
} from "../../core/api/useCompanies";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useAuthToken } from "../../core/data/redux/useAuthToken";

interface ClientExportImportProps {
  selectedClientIds?: string[];
  onRefreshData?: () => Promise<void>;
  canExport: boolean;
}

const ClientExportImport: React.FC<ClientExportImportProps> = ({
  selectedClientIds = [],
  onRefreshData,
  canExport,
}) => {
  const empToken = useAuthToken();
  // Modal states
  const [showImportModal, setShowImportModal] = useState(false);

  // Dropdown states
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);

  // Import states
  const [importType, setImportType] = useState<"update" | "post">("update");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Download Demo Template (now XLSX instead of CSV)
  const downloadDemoTemplate = () => {
    const updateColumns = [
      "Client ID",
      "Company Licence",
      "Company Name",
      "Email",
      "Server Name",
      "Company Batch",
      "Contact Number",
      "Product",
      "Modules",
      "Status",
    ];

    const newColumns = [
      "Company Licence",
      "Company Name",
      "Email",
      "Server Name",
      "Company Batch",
      "Contact Number",
      "Product",
      "Modules",
      "Status",
    ];

    const columns = importType === "update" ? updateColumns : newColumns;

    // Sample data for demo
    const sampleData =
      importType === "update"
        ? [
            [
              "CLI001",
              "LIC001",
              "ABC Technologies",
              "abc@example.com",
              "server1.example.com",
              "Gold",
              "9876543210",
              "TMS Pro",
              "Module1,Module2",
              "Active",
            ],
            [
              "CLI002",
              "LIC002",
              "XYZ Solutions",
              "xyz@example.com",
              "server2.example.com",
              "Silver",
              "9876543211",
              "TMS Basic",
              "Module3",
              "Active",
            ],
          ]
        : [
            [
              "LIC001",
              "ABC Technologies",
              "abc@example.com",
              "server1.example.com",
              "Gold",
              "9876543210",
              "TMS Pro",
              "Module1,Module2",
              "Active",
            ],
            [
              "LIC002",
              "XYZ Solutions",
              "xyz@example.com",
              "server2.example.com",
              "Silver",
              "9876543211",
              "TMS Basic",
              "Module3",
              "Active",
            ],
          ];

    const worksheet = XLSX.utils.aoa_to_sheet([columns, ...sampleData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    const fileName =
      importType === "update"
        ? "client_update_template.xlsx"
        : "client_new_template.xlsx";

    XLSX.writeFile(workbook, fileName);
  };

  // Export handlers
  const handleExportAllExcel = async () => {
    if (!canExport) {
      toast.warning("You don't have permission to export companies.");
      setShowExportDropdown(false);
      return;
    }
    try {
      await exportAllClientsExcel(empToken);
    } catch (error) {
      console.error("Export all Excel failed:", error);
    }
    setShowExportDropdown(false);
  };

  const handleExportAllPdf = async () => {
    if (!canExport) {
      toast.warning("You don't have permission to export companies.");
      setShowExportDropdown(false);
      return;
    }
    try {
      await exportAllClientsPdf(empToken);
    } catch (error) {
      console.error("Export all PDF failed:", error);
    }
    setShowExportDropdown(false);
  };

  const handleExportSelectedExcel = async () => {
    if (!canExport) {
      toast.warning("You don't have permission to export companies.");
      setShowExportDropdown(false);
      return;
    }
    if (selectedClientIds.length === 0) {
      toast.warning("Please select at least one client to export");
      return;
    }
    try {
      await exportSelectedClientsExcel(selectedClientIds, empToken);
    } catch (error) {
      console.error("Export selected Excel failed:", error);
    }
    setShowExportDropdown(false);
  };

  const handleExportSelectedPdf = async () => {
    if (!canExport) {
      toast.warning("You don't have permission to export companies.");
      setShowExportDropdown(false);
      return;
    }
    if (selectedClientIds.length === 0) {
      toast.warning("Please select at least one client to export");
      return;
    }
    try {
      await exportSelectedClientsPdf(selectedClientIds, empToken);
    } catch (error) {
      console.error("Export selected PDF failed:", error);
    }
    setShowExportDropdown(false);
  };

  // Import handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.warning("Please select a file to import");
      return;
    }

    setIsUploading(true);
    try {
      if (importType === "update") {
        await updateClientsFromExcel(selectedFile, empToken);
      } else {
        await importClientsFromExcel(selectedFile, empToken);
      }

      setShowImportModal(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onRefreshData) {
        await onRefreshData();
      }
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsUploading(false);
      setShowUploadDropdown(false);
    }
  };

  return (
    <>
      {/* Export/Upload Button Group */}
      <div className="me-2 mb-2">
        <div className="d-flex align-items-center border bg-white rounded p-1 gap-1">
          {/* Export Dropdown */}
          <div className="position-relative">
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 px-2"
              style={{ height: "31px" }}
              onClick={() => {
                setShowExportDropdown(!showExportDropdown);
                setShowUploadDropdown(false);
              }}
            >
              <i className="ti ti-file-export" />
              Export
            </button>
            {showExportDropdown && (
              <div
                className="dropdown-menu show dropdown-menu-end p-2 shadow"
                style={{ minWidth: "240px" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Export Options</h6>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setShowExportDropdown(false)}
                  >
                    ×
                  </button>
                </div>

                <button className="dropdown-item" onClick={handleExportAllExcel}>
                  <i className="ti ti-file-type-xls me-2" /> Export All as Excel
                </button>

                <button className="dropdown-item" onClick={handleExportAllPdf}>
                  <i className="ti ti-file-type-pdf me-2" /> Export All as PDF
                </button>
                <button
                  className="dropdown-item"
                  onClick={handleExportSelectedExcel}
                  disabled={selectedClientIds.length === 0}
                >
                  <i className="ti ti-file-type-xls me-2" /> Selected Excel (
                  {selectedClientIds.length})
                </button>

                <button
                  className="dropdown-item"
                  onClick={handleExportSelectedPdf}
                  disabled={selectedClientIds.length === 0}
                >
                  <i className="ti ti-file-type-pdf me-2" /> Selected PDF (
                  {selectedClientIds.length})
                </button>
              </div>
            )}
          </div>

          {/* Upload Dropdown */}
          <div className="position-relative">
            <button
              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 px-2"
              style={{ height: "31px" }}
              onClick={() => {
                setShowUploadDropdown(!showUploadDropdown);
                setShowExportDropdown(false);
              }}
            >
              <i className="ti ti-upload" />
              Upload
            </button>
            {showUploadDropdown && (
              <div
                className="dropdown-menu show dropdown-menu-end p-2 shadow"
                style={{ minWidth: "220px" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Import Options</h6>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setShowUploadDropdown(false)}
                  >
                    ×
                  </button>
                </div>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setImportType("update");
                    setShowImportModal(true);
                    setShowUploadDropdown(false);
                  }}
                >
                  <i className="ti ti-edit me-2" /> Import (Update)
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setImportType("post");
                    setShowImportModal(true);
                    setShowUploadDropdown(false);
                  }}
                >
                  <i className="ti ti-plus me-2" /> All Import (New)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {importType === "update"
                    ? "Import (Update Clients)"
                    : "All Import (Add New Clients)"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Upload Instructions */}
                <div className="alert alert-info mb-3">
                  <h6 className="alert-heading mb-2">
                    <i className="ti ti-info-circle me-1" />
                    Upload Instructions
                  </h6>
                  {importType === "update" ? (
                    <div>
                      <p className="mb-2">
                        <strong>For Update Import:</strong>
                      </p>
                      <ul className="mb-0 ps-3">
                        <li>
                          <strong>Client ID is mandatory</strong> - Without
                          client_id, the update operation cannot be performed
                        </li>
                        <li>Only include columns you want to update</li>
                        <li>
                          Keep the same column headers as in the export file
                        </li>
                        <li>
                          Ensure client_id matches existing clients in the
                          system
                        </li>
                        <li>
                          Empty cells will not update the corresponding fields
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <div>
                      <p className="mb-2">
                        <strong>For New Import:</strong>
                      </p>
                      <ul className="mb-0 ps-3">
                        <li>
                          <strong>Required columns:</strong> Company Name,
                          Company Licence, Product, Module
                        </li>
                        <li>
                          Use correct column names: Company Licence, Company
                          Name, Email, Server Name, etc.
                        </li>
                        <li>
                          Email must be unique and in valid format or optional
                        </li>
                        <li>
                          Phone numbers should be in proper format or optional
                        </li>
                        <li>
                          Status should be either 'Active' or 'In Active'
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Select Excel File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                  />
                  <div className="form-text">
                    {importType === "update"
                      ? "Upload Excel file to update existing client data. Client ID is required for updates."
                      : "Upload Excel file to add new clients. Ensure all required columns are present with valid data."}
                  </div>
                </div>

                {selectedFile && (
                  <div className="alert alert-success">
                    <i className="ti ti-file-check me-1" />
                    Selected: {selectedFile.name}
                    <br />
                    <small>
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </small>
                  </div>
                )}

                {/* Download Demo Template */}
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Demo Excel Template:</h6>
                    <button
                      type="button"
                      className="btn btn-outline-info btn-sm"
                      onClick={downloadDemoTemplate}
                    >
                      <i className="ti ti-download me-1" />
                      Download Demo Excel
                    </button>
                  </div>
                  <div className="alert alert-light">
                    <small className="text-muted">
                      Download the demo Excel template with correct column
                      headers and sample data format.
                      {importType === "update"
                        ? " This template includes Client ID which is mandatory for updates."
                        : " This template is for adding new clients to the system."}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                  onClick={handleImport}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload me-1" />
                      {importType === "update" ? "Update Data" : "Import Data"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientExportImport;